// ─── Une bulle de message ─────────────────────────────────────────
// Ce composant ne DÉCIDE de rien : il rend l'état calculé par
// `services/affichageMessage.js`. Toute la logique (qui voit quoi, quelles
// actions, quelle couleur) y vit et y est testée.
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image, Linking } from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { vueMessage, actionsMessage, clePlaceholder, fondMessage } from '../services/affichageMessage';
import { apercuCarte, lienCarte } from './piecesJointes';

// Nom affiché d'un fichier joint : celui de l'upload, sinon dérivé du chemin de
// stockage — qui porte toujours l'extension. Sans ce repli, un document
// s'affichait « Document » sans dire de quel type il s'agit.
function nomFichier(media) {
  const brut = (media?.nom || '').trim();
  if (brut) return brut;
  const dernier = String(media?.chemin || '').split('/').pop() || '';
  return dernier.split('?')[0] || 'Document';
}

const heure = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const memeJour = d.toDateString() === new Date().toDateString();
  const hm = `${String(d.getHours()).padStart(2, '0')}h${String(d.getMinutes()).padStart(2, '0')}`;
  return memeJour ? hm : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${hm}`;
};

export default function Bulle({ msg, uid, disc, onAction }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => styles(UI), [UI]);
  const vue = vueMessage(msg, uid, disc);
  const actions = actionsMessage(msg, uid, disc);
  const fond = fondMessage(vue);
  const aMoi = msg.auteurUid === uid;

  // Contenu masqué : une ligne discrète, jamais la bulle. Le libellé DIFFÈRE
  // selon la cause — « retiré », « modéré » et « supprimé » ne disent pas la
  // même chose au lecteur.
  if (!vue.visible) {
    return (
      <View style={[s.ligne, aMoi ? s.aDroite : s.aGauche]}>
        <Text style={s.placeholder}>{t(clePlaceholder(vue))}</Text>
      </View>
    );
  }

  const bordure = fond === 'alerte' ? UI.danger
    : fond === 'attention' ? COULEUR_ATTENTION
      : null;

  return (
    <View style={[s.ligne, aMoi ? s.aDroite : s.aGauche]}>
      <TouchableOpacity
        activeOpacity={0.8}
        onLongPress={() => onAction?.(msg, actions)}
        style={[
          s.bulle,
          aMoi ? s.bulleMoi : s.bulleAutre,
          bordure && { borderColor: bordure, borderWidth: 1.5 },
        ]}
      >
        {!aMoi && <Text style={s.auteur}>{msg.auteurNom || t('messagerie.quelquun')}</Text>}

        {msg.type === 'image' && !!msg.media?.url && (
          <Image source={{ uri: msg.media.url }} style={s.image} resizeMode="cover" />
        )}

        {msg.type === 'fichier' && !!msg.media?.url && (
          <TouchableOpacity onPress={() => Linking.openURL(msg.media.url)} style={s.fichier}>
            <Text style={s.fichierTxt} numberOfLines={1}>📎 {nomFichier(msg.media)}</Text>
          </TouchableOpacity>
        )}

        {msg.type === 'position' && !!msg.position && (
          <TouchableOpacity onPress={() => Linking.openURL(lienCarte(msg.position.lat, msg.position.lng))}>
            <Image
              source={{ uri: apercuCarte(msg.position.lat, msg.position.lng) }}
              style={s.carte}
              resizeMode="cover"
            />
            <Text style={s.fichierTxt}>📍 {t('messagerie.voirSurCarte')}</Text>
          </TouchableOpacity>
        )}

        {!!msg.texte && <Text style={s.texte}>{msg.texte}</Text>}

        {/* Pourquoi cette bulle est encadrée — sinon la couleur seule est muette. */}
        {fond === 'alerte' && (
          <Text style={[s.mention, { color: UI.danger }]}>
            {t(vue.role === 'auteur' ? 'messagerie.mentionModereMoi' : 'messagerie.mentionModereAutres')}
          </Text>
        )}
        {fond === 'attention' && (
          <Text style={[s.mention, { color: COULEUR_ATTENTION }]}>
            {t('messagerie.mentionSignale')}
          </Text>
        )}

        <Text style={s.heure}>{heure(msg.creeLe)}</Text>
      </TouchableOpacity>
    </View>
  );
}

// Le thème n'a pas de jeton « attention » (il a `danger` pour le rouge). On le
// pose ici en attendant qu'il en porte un — pas dans chaque écran.
export const COULEUR_ATTENTION = '#F57C00';

const styles = (UI) => StyleSheet.create({
  ligne: { paddingHorizontal: 10, marginBottom: 6, flexDirection: 'row' },
  aGauche: { justifyContent: 'flex-start' },
  aDroite: { justifyContent: 'flex-end' },
  bulle: { maxWidth: '82%', borderRadius: 14, paddingHorizontal: 12, paddingVertical: 8 },
  bulleMoi: { backgroundColor: UI.accentTint, borderTopRightRadius: 4 },
  bulleAutre: { backgroundColor: UI.champ, borderTopLeftRadius: 4 },
  auteur: { fontSize: 11.5, fontWeight: '800', color: UI.muted, marginBottom: 2 },
  texte: { fontSize: 15, color: UI.ink, lineHeight: 20 },
  image: { width: 220, height: 165, borderRadius: 10, marginBottom: 4 },
  carte: { width: 220, height: 110, borderRadius: 10, marginBottom: 4 },
  fichier: { paddingVertical: 4 },
  fichierTxt: { fontSize: 13.5, color: UI.accent, fontWeight: '600' },
  mention: { fontSize: 11.5, fontWeight: '700', marginTop: 4 },
  heure: { fontSize: 10.5, color: UI.faint, alignSelf: 'flex-end', marginTop: 3 },
  placeholder: {
    fontSize: 12.5, color: UI.faint, fontStyle: 'italic',
    paddingHorizontal: 12, paddingVertical: 6,
  },
});
